import {
  NotificationHookFragment,
  useCreateDeviceMutation,
  useFindNotificationLazyQuery,
} from '@/generated/graphql';
import {Close} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Slide,
  SlideProps,
  Snackbar,
  Typography,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {captureException} from '@sentry/react';
import {toError} from '@ark/react';
import {getToken, MessagePayload, onMessage} from 'firebase/messaging';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useBrand} from '@/contexts/BrandContext';
import notificationSoundSrc from '../assets/sound/notification.mp3';
import {messaging} from '../firebase';
import {useLogEvent} from './useLogEvent/useLogEvent';
import {MANAGER_REPORT_EXTRACTION_CATEGORY} from './useLogEvent/eventTypes/TasksEvents';
import {
  type AudibleCategory,
  getAudibleEnabledForCategory,
  getAudibleNotificationEnabled,
} from './useAudibleNotification';

let audioContext: AudioContext | null = null;
let audioBuffer: AudioBuffer | null = null;

export const initAudioContext = async () => {
  if (audioContext) return;
  audioContext = new AudioContext();
  const response = await fetch(notificationSoundSrc);
  const arrayBuffer = await response.arrayBuffer();
  audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
};

const playSound = async () => {
  if (!audioContext || !audioBuffer) return;
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }
  const source = audioContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(audioContext.destination);
  source.start(0);
};

const backendCategoryToAudible: Partial<Record<string, AudibleCategory>> = {
  MANAGER_AMENITY: 'amenity',
  MANAGER_APPLICATION: 'application',
  MANAGER_APPLICATION_MESSAGE: 'application_message',
  MANAGER_COMMUNITY_EVENT: 'community_event',
  MANAGER_COMMUNITY_PRODUCT: 'community_product',
  MANAGER_COMMUNITY_UPDATE: 'community_update',
  MANAGER_CONVERSATION_ASSIGNMENT: 'conversation_assignment',
  MANAGER_TEAM_ASSIGNMENT: 'team_assignment',
  MANAGER_CONVERSATION: 'conversation',
  MANAGER_SURVEYS: 'survey',
  MANAGER_TASK_ASSIGNMENT: 'task_assignment',
  MANAGER_TASK_UPDATE: 'task_update',
  MANAGER_TASK_COMMENT: 'task_comment',
};

const playNotificationSound = async (category?: AudibleCategory) => {
  if (!getAudibleEnabledForCategory(category)) return;
  await initAudioContext();
  await playSound();
};

export const previewNotificationSound = async () => {
  await initAudioContext();
  await playSound();
};

const SlideDown = (props: SlideProps) => <Slide {...props} direction="down" />;

export const useNotifications = () => {
  const [createNewDevice] = useCreateDeviceMutation();
  const status = window.Notification?.permission;
  const isSubscribingRef = useRef(false);

  const subscribe = useCallback(async () => {
    if (isSubscribingRef.current) {
      return;
    }

    isSubscribingRef.current = true;
    try {
      const msg = messaging();
      if (!msg) return;
      const token = await getToken(msg, {
        vapidKey: import.meta.env.VITE_VAPID_KEY,
      });
      if (token) {
        await createNewDevice({
          variables: {
            input: {
              token,
              platform: 'manager-dashboard',
              type: 'web',
            },
          },
        });
      }
    } catch (e) {
      captureException(toError(e));
    } finally {
      setTimeout(() => {
        isSubscribingRef.current = false;
      }, 1000);
    }
  }, [createNewDevice]);

  useEffect(() => {
    if (status === 'granted') {
      subscribe();
    }
  }, [status, subscribe]);

  return {status, subscribe};
};

type ListenerType = (notification: NotificationHookFragment) => void;

const notificationContext = createContext<
  | {
      addListener: (
        listener: ListenerType,
        persistedName?: string,
      ) => () => void;
      notify: (payload: MessagePayload) => void;
    }
  | undefined
>(undefined);

type ToastState = {
  notification: NotificationHookFragment | null;
  body: string;
};

type NotificationProviderProps = {
  children: ReactNode;
  onNotificationClick?: (notification: NotificationHookFragment) => void;
  shouldSuppress?: (notification: NotificationHookFragment) => boolean;
};

export const NotificationContext = ({
  children,
  onNotificationClick,
  shouldSuppress,
}: NotificationProviderProps) => {
  const theme = useTheme();
  const logEvent = useLogEvent();
  const {name: brandName, assets} = useBrand();
  const [listeners, setListeners] = useState<ListenerType[]>([]);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [fetchNotification] = useFindNotificationLazyQuery();
  const [, setPersistedNames] = useState<string[]>([]);

  useEffect(() => {
    if (!getAudibleNotificationEnabled()) return;
    const unlock = () => {
      initAudioContext();
      document.removeEventListener('click', unlock);
    };
    document.addEventListener('click', unlock);
    return () => document.removeEventListener('click', unlock);
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel('ark-notifications');
    channel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        const category =
          backendCategoryToAudible[event.data?.notificationCategory ?? ''];
        playNotificationSound(category);
      }
    };
    return () => channel.close();
  }, []);

  const addListener = useCallback(
    (listener: ListenerType, persistedName?: string) => {
      if (!persistedName) {
        setListeners(listeners => [...listeners, listener]);
        return () => {
          setListeners(listeners => listeners.filter(l => l !== listener));
        };
      } else {
        setPersistedNames(names => {
          if (names.includes(persistedName)) {
            return names;
          }
          setListeners(listeners => [...listeners, listener]);
          return [...names, persistedName];
        });
      }
      return () => undefined;
    },
    [],
  );

  const notify = useCallback(
    async (payload: MessagePayload) => {
      const category = backendCategoryToAudible[payload?.data?.category ?? ''];
      playNotificationSound(category);
      if (
        payload?.data?.category === MANAGER_REPORT_EXTRACTION_CATEGORY &&
        payload?.data?.id
      ) {
        logEvent({
          name: 'Inspection Report Extraction - Notification Delivered',
          data: {notificationId: payload.data.id},
        });
      }
      const pushBody =
        payload?.notification?.body ||
        payload?.notification?.title ||
        'Open to view';
      if (payload?.data?.id) {
        try {
          const data = await fetchNotification({
            variables: {id: payload.data.id},
          });
          const notification = data.data?.notification ?? null;
          if (notification) {
            listeners.forEach(listener => listener(notification));
            if (!shouldSuppress?.(notification)) {
              setToast({notification, body: pushBody});
            }
          } else {
            setToast({notification: null, body: pushBody});
          }
        } catch {
          setToast({notification: null, body: pushBody});
        }
      } else {
        setToast({notification: null, body: pushBody});
      }
    },
    [listeners, fetchNotification, shouldSuppress, logEvent],
  );

  useEffect(() => {
    const msg = messaging();
    if (!msg) return;
    return onMessage(msg, payload => {
      notify(payload);
    });
  }, [notify]);

  const handleToastClick = () => {
    if (toast?.notification && onNotificationClick) {
      onNotificationClick(toast.notification);
    }
    setToast(null);
  };

  const canClickToast = !!toast?.notification && !!onNotificationClick;

  return (
    <notificationContext.Provider value={{addListener, notify}}>
      {children}
      <Snackbar
        open={!!toast}
        onClose={() => setToast(null)}
        autoHideDuration={5000}
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        TransitionComponent={SlideDown}
        sx={{
          zIndex: 99999,
          top: 'calc(env(safe-area-inset-top, 0px) + 4px) !important',
          mt: 1,
        }}
      >
        <Box
          role={canClickToast ? 'button' : undefined}
          tabIndex={canClickToast ? 0 : undefined}
          onClick={canClickToast ? handleToastClick : undefined}
          onKeyDown={
            canClickToast
              ? e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToastClick();
                  }
                }
              : undefined
          }
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: `1px solid ${theme.palette.divider}`,
            p: 2,
            maxWidth: 380,
            width: '100%',
            cursor: canClickToast ? 'pointer' : 'default',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': canClickToast
              ? {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 12px 36px rgba(0, 0, 0, 0.16)',
                }
              : undefined,
          }}
        >
          <img
            src={assets.symbol}
            alt={brandName}
            style={{width: 28, height: 28, flexShrink: 0, marginTop: 2}}
          />
          <Box sx={{flex: 1, minWidth: 0}}>
            <Typography
              sx={{fontSize: 14, fontWeight: 600, lineHeight: 1.3, mb: 0.25}}
            >
              {brandName}
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 400,
                lineHeight: 1.4,
                color: theme.palette.text.secondary,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {toast?.body}
            </Typography>
          </Box>
          <IconButton
            onClick={e => {
              e.stopPropagation();
              setToast(null);
            }}
            size="small"
            sx={{
              p: 0.25,
              mt: -0.25,
              mr: -0.5,
              color: theme.palette.text.secondary,
            }}
          >
            <Close sx={{fontSize: 16}} />
          </IconButton>
        </Box>
      </Snackbar>
    </notificationContext.Provider>
  );
};

export const useSubscribeToNotifications = (
  callback: (notification?: NotificationHookFragment) => void | Promise<void>,
  persistedName?: string,
) => {
  const context = useContext(notificationContext);

  const savedListener = useRef<undefined | ListenerType>(undefined);

  useEffect(() => {
    savedListener.current = callback;
  }, [callback]);

  useEffect(() => {
    return context?.addListener(
      notification =>
        savedListener.current && savedListener.current(notification),
      persistedName,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedListener, persistedName]);

  if (!context) {
    console.warn(
      'useSubscribeToNotifications must be used within a NotificationContext',
    );
  }
};
